"use client";

import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import type { Editor } from "@tiptap/react";
import { useChapterRecovery } from "@/hooks/use-chapter-recovery";
import { useAutosave } from "@/hooks/use-autosave";
import { useEditorBackend } from "@/lib/editor-backend/context";
import { useEditorStore } from "@/stores/editor-store";
import { extractMentionIds } from "@/components/editor/extensions/lore-mention";
import { sanitizeTiptapJson } from "@/lib/utils";
import { getChapterContentHash } from "@/lib/editor/chapter-content-version";
type ChapterStatus = "draft" | "published";
const DRAFT_AUTOSAVE_DELAY_MS = 2500;
const DRAFT_AUTOSAVE_MIN_INTERVAL_MS = 5000;
function saveRequestFailed(): never {
  throw new Error("Couldn't save this chapter. Check your connection and try again.");
}
const countWords = (text: string) => text.trim().split(/\s+/).filter(Boolean).length;
export type ChapterSaveController = ReturnType<typeof useChapterSave>;
export function useChapterSave({
  editor,
  chapterId,
  initialStatus,
  initialContent = editor?.getJSON() ?? {},
  recoveryKey = chapterId,
  manageStore = true,
  syncMentions = true
}: {
  editor: Editor | null;
  chapterId: string;
  initialStatus: ChapterStatus;
  initialContent?: Record<string, unknown> | null;
  recoveryKey?: string;
  manageStore?: boolean;
  syncMentions?: boolean;
}) {
  const backend = useEditorBackend();
  const recovery = useChapterRecovery(recoveryKey, editor, initialContent);
  const [wordCount, setWordCount] = useState(0);
  const {
    recordRecovery,
    acknowledgeSaved
  } = recovery;
  const savedMentions = useRef<string | null>(null);
  const savedContent = useRef(initialContent);
  const active = useRef(true);
  const manualSave = useRef<Promise<boolean> | null>(null);
  useEffect(() => {
    active.current = true;
    return () => {
      active.current = false;
    };
  }, []);
  const [status, setStatus] = useState(initialStatus);
  const [isChangingStatus, setChangingStatus] = useState(false);
  const [statusError, setStatusError] = useState<string | null>(null);
  const changing = useRef(false);
  const navigation = useRef<Promise<boolean> | null>(null);
  const statusRef = useRef(status);
  const snapshot = useRef<{
    content: Record<string, unknown>;
    wordCount: number;
  } | null>(null);
  useLayoutEffect(() => {
    statusRef.current = status;
  }, [status]);
  useEffect(() => {
    setStatus(initialStatus);
  }, [initialStatus]);
  const persist = useCallback(async ({
    automatic
  }: {
    automatic: boolean;
  }) => {
    if (!active.current) throw new Error("This editor has been closed.");
    if (automatic && statusRef.current !== "draft") throw new Error("Save published changes manually.");
    const current = snapshot.current;
    if (!current) throw new Error("The editor is not ready. Please try again.");
    const expectedContentHash = await getChapterContentHash(savedContent.current);
    if (!active.current) throw new Error("This editor has been closed.");
    const result = await backend.saveChapterContent(chapterId, current.content, current.wordCount, {
      autosave: automatic,
      expectedContentHash
    }).catch(saveRequestFailed);
    if (!result.success) throw new Error(result.error ?? "Couldn't save this chapter. Please try again.");
    // Advance even if mention sync fails: prose already committed, and a
    // retry must compare against that acknowledged server snapshot.
    savedContent.current = current.content;
    if (!active.current) throw new Error("This editor has been closed.");
    const mentionIds = extractMentionIds(current.content).sort();
    const mentionKey = JSON.stringify(mentionIds);
    if (syncMentions && savedMentions.current !== mentionKey) {
      const mentions = await backend.syncChapterMentions(chapterId, mentionIds).catch(saveRequestFailed);
      if (mentions.error) throw new Error(mentions.error);
      savedMentions.current = mentionKey;
      useEditorStore.getState().bumpLoreRefresh();
    }
    if (active.current) acknowledgeSaved(current.content);
  }, [acknowledgeSaved, backend, chapterId, syncMentions]);
  const autosave = useAutosave({
    persist,
    enabled: status === "draft" && !isChangingStatus,
    delay: DRAFT_AUTOSAVE_DELAY_MS,
    minInterval: DRAFT_AUTOSAVE_MIN_INTERVAL_MS
  });
  const {
    markDirty,
    flush,
    cancelPending,
    hasPendingChanges,
    waitForPending
  } = autosave;
  useEffect(() => {
    if (!editor) return;
    const capture = () => {
      const wordCount_0 = countWords(editor.getText());
      snapshot.current = {
        content: sanitizeTiptapJson(editor.getJSON()),
        wordCount: wordCount_0
      };
      setWordCount(wordCount_0);
      if (manageStore) useEditorStore.getState().setWordCount(wordCount_0);
    };
    const update = () => {
      capture();
      if (snapshot.current) recordRecovery(snapshot.current.content);
      markDirty();
    };
    capture();
    editor.on("update", update);
    return () => {
      editor.off("update", update);
    };
  }, [editor, markDirty, recordRecovery, manageStore]);
  useLayoutEffect(() => {
    if (manageStore) useEditorStore.setState({
      isDirty: autosave.isDirty,
      isSaving: autosave.isSaving
    });
  }, [autosave.isDirty, autosave.isSaving, manageStore]);
  const handleSave = useCallback(() => {
    if (changing.current) return Promise.resolve(false);
    if (manualSave.current) return manualSave.current;
    // Published saves approve one snapshot. Later typing stays dirty until
    // another explicit Save; repeated shortcuts cannot queue extra writes.
    const request = flush({
      force: true,
      drain: statusRef.current === "draft"
    });
    manualSave.current = request;
    void request.finally(() => {
      if (manualSave.current === request) manualSave.current = null;
    });
    return request;
  }, [flush]);
  const changeStatus = useCallback(async (next: ChapterStatus) => {
    if (!editor || changing.current) return false;
    changing.current = true;
    setChangingStatus(true);
    setStatusError(null);
    cancelPending();
    const wasEditable = editor.isEditable;
    editor.setEditable(false, false);
    try {
      // Published edits require Save; unpublishing itself must not save them.
      if (next === "published" && !(await flush())) return false;
      if (next === "draft") await waitForPending();
      const result_0 = await backend.updateChapterMeta(chapterId, {
        status: next
      });
      if (result_0.error) throw new Error(result_0.error);
      statusRef.current = next;
      setStatus(next);
      return true;
    } catch (error) {
      setStatusError(error instanceof Error ? error.message : "Couldn't update publication status.");
      return false;
    } finally {
      cancelPending();
      changing.current = false;
      setChangingStatus(false);
      if (!editor.isDestroyed) editor.setEditable(wasEditable, false);
    }
  }, [backend, cancelPending, chapterId, editor, flush, waitForPending]);
  const beforeNavigate = useCallback((): Promise<boolean> => {
    if (navigation.current) return navigation.current;
    const request_0 = (async () => {
      if (changing.current) return false;
      if (!hasPendingChanges()) return true;
      if (statusRef.current === "published") {
        return window.confirm("You have unsaved published changes. Leave without saving?");
      }
      const wasEditable_0 = editor?.isEditable ?? false;
      editor?.setEditable(false, false);
      try {
        return await flush({
          automatic: true
        });
      } finally {
        if (editor && !editor.isDestroyed) editor.setEditable(wasEditable_0, false);
      }
    })();
    navigation.current = request_0;
    void request_0.finally(() => {
      if (navigation.current === request_0) navigation.current = null;
    });
    return request_0;
  }, [editor, flush, hasPendingChanges]);
  const saveDraft = useCallback(() => {
    if (!changing.current && statusRef.current === "draft") void flush({
      automatic: true
    });
  }, [flush]);
  const hasUnsavedChanges = useCallback(() => changing.current || hasPendingChanges(), [hasPendingChanges]);
  const getSavedContent = useCallback(() => savedContent.current, []);
  const getCurrentContent = useCallback(() => snapshot.current?.content ?? savedContent.current, []);
  const setEditable = useCallback((editable: boolean) => {
    if (editor && !editor.isDestroyed) editor.setEditable(editable, false);
  }, [editor]);
  const focusEditor = useCallback(() => {
    if (editor && !editor.isDestroyed) editor.commands.focus();
  }, [editor]);
  const restoreContent = useCallback((content: Record<string, unknown>) => {
    if (!editor || editor.isDestroyed || changing.current) return false;
    // setContent emits the normal update, retaining the destination
    // version's saved baseline while recording its own recovery copy.
    return editor.commands.setContent(content);
  }, [editor]);
  return {
    ...autosave,
    ...recovery,
    status,
    isChangingStatus,
    statusError,
    handleSave,
    changeStatus,
    beforeNavigate,
    saveDraft,
    hasUnsavedChanges,
    wordCount,
    getSavedContent,
    getCurrentContent,
    setEditable,
    focusEditor,
    restoreContent
  };
}